import axios from "axios";
import { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import server from "../environment";

export const AuthContext = createContext({});

const client = axios.create({
  baseURL: `${server}/api/v1/users`,
});

// Attach JWT to every request automatically
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle global 401 responses (token expired)
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [userData, setUserData] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const navigate = useNavigate();

  const handleRegister = useCallback(async (name, username, password) => {
    const response = await client.post("/register", { name, username, password });
    return response.data.message;
  }, []);

  const handleLogin = useCallback(async (username, password) => {
    const response = await client.post("/login", { username, password });
    const { token, user } = response.data;

    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    setUserData(user);

    navigate("/home");
    return user;
  }, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUserData(null);
    navigate("/auth");
  }, [navigate]);

  const getHistoryOfUser = useCallback(async () => {
    const response = await client.get("/activity");
    return response.data.data;
  }, []);

  const addToUserHistory = useCallback(async (meetingCode) => {
    const response = await client.post("/activity", { meeting_code: meetingCode });
    return response.data;
  }, []);

  const isAuthenticated = useCallback(() => {
    return Boolean(localStorage.getItem("token"));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        userData,
        setUserData,
        handleRegister,
        handleLogin,
        handleLogout,
        getHistoryOfUser,
        addToUserHistory,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);